<template>
  <zm-dialog
    class="opc-aic-pro-union-dialog"
    :class="{
      'opc-modal-dialog': isLegacyModalUI
    }"
    :show-close="!isLegacyModalUI"
    v-model="visible"
    title="Select a plan"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="dialog-content">
      <plan-card
        v-for="plan in plans"
        :key="plan.id"
        :plan="plan"
        :loading-id="loadingId"
        :is-popular="plan.isPopular"
        @buyNow="handleBuy"
        @close="visible = false"
      />
    </div>
  </zm-dialog>
</template>

<script>
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import PlanCard from '@/components/plan-card.vue';
import { planUnitMap } from '@/common/product-unit';
import { AIC_UNIFIED_LINK } from '@/common/unified-link';
import { MODAL_QUERY_PARAMS, PageTypes, URL_PAGE_TYPE_KEY } from '@/common/constants';
import { navigateCurrentWindowTo } from '@/utils/modal-utils.js';
import { enableCAICPlanRebranding } from '@/plans/ZOOM_CUSTOM_AI_COMPANION/utils.js';

const PLAN_ID_MAP = {
  PRO: 'workplace-pro',
  AIC: 'ai-companion',
};

export default {
  name: 'AICProDialog',
  components: {
    PlanCard,
  },
  mixins: [ConfirmComponent],
  data() {
    return {
      loading: false,
      visible: false,
      loadingId: null,
      trackingPlanName: 'AI Companion',
      proPlan: {
        id: PLAN_ID_MAP.PRO,
        trackingName: 'zoom workplace pro',
        iconName: 'zoom',
        name: this.$t('billing.Workplace'),
        title: this.$t('common.pro'),
        description: this.$t('billing.zone.pro_desc'),
        priceKey: 'zo_pro_yearly',
        priceUnit: planUnitMap.user,
        isPopular: true,
        highlightsText: this.$t('billing.zoom_ai_companion.ai_highlights'),
        highlights: [
          {
            category: this.$t('billing.zone.meetings'),
            categoryIconName: 'meetings',
            features: [
              {
                text: this.$t('billing.comparison.30_hours'),
                bold: true,
                suffix: this.$t('billing.zone.edu_details.attendees'),
              },
              {
                text: this.$t('billing.zone.per_meeting_participants'),
                link: {
                  text: this.$t('billing.large_meeting.increase_with'),
                  event: 'addLargeMeeting',
                },
              },
            ],
          },
          {
            category: this.$t('marketing.new_pricing.clips'),
            categoryIconName: 'clips',
            features: [
              { text: this.$t('marketing.new_pricing.clips_unlimited') },
              { text: this.$t('marketing.new_pricing.clips_avatars') },
            ],
          },
          {
            category: this.$t('mail'),
            categoryIconName: 'mail',
            features: [{ text: this.$t('zoom_mail') }],
          },
        ],
      },
    };
  },
  computed: {
    isLegacyModalUI() {
      return this.$context?.is?.modal && !this.$context?.is?.modal2Column;
    },
    enableCAICToStandalone() {
      return window.newBillingConfig?.enableCAICToStandalone === true;
    },
    aicPlan() {
      const categoryName = enableCAICPlanRebranding
        ? this.$t('billing.zoom_ai_companion.rebranding_title')
        : this.$t('billing.zoom_ai_companion.title');
      if (this.enableCAICToStandalone) {
        return {
          id: PLAN_ID_MAP.AIC,
          trackingName: 'zoom AI Companion',
          iconName: 'ai-companion',
          name: categoryName,
          title: categoryName,
          description: this.$t('billing.zoom_ai_companion.desc'),
          priceKey: 'zm_ai_companion_yearly',
          priceUnit: planUnitMap.user,
          isPopular: false,
          highlightsText: this.$t('billing.common.highlights'),
          highlights: [
            {
              category: categoryName,
              categoryIconName: 'aic',
              footnote: this.$t('billing.zoom_ai_companion.new_plan_card.footnote'),
              features: [
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item1') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item2') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item3') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item4') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item5') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item6') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item7') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item8') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item9') },
                { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item10') },
              ],
            },
            { type: 'divide' },
            {
              category: this.$t('billing.zone.meetings'),
              categoryIconName: 'meetings',
              features: [
                { text: this.$t('billing.zone.per_meeting_minutes') },
                { text: this.$t('billing.zone.per_meeting_participants') },
              ],
            },
          ],
        };
      }
      return {
        id: PLAN_ID_MAP.AIC,
        trackingName: 'zoom AI Companion',
        iconName: 'ai-companion',
        name: categoryName,
        title: categoryName,
        description: this.$t('billing.zoom_ai_companion.desc'),
        priceKey: 'zm_ai_companion_yearly',
        priceUnit: planUnitMap.user,
        isPopular: false,
        highlightsText: this.$t('billing.common.highlights'),
        highlights: [
          {
            category: categoryName,
            categoryIconName: 'aic',
            footnote: this.$t('billing.zoom_ai_companion.new_plan_card.footnote'),
            features: [
              {
                title: `${this.$t('billing.zoom_ai_companion.new_plan_card.highlight_title')} 🚀`,
                text: this.$t('billing.zoom_ai_companion.new_plan_card.highlight_description'),
              },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item1') },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item2') },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item3') },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item5') },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item6') },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item7') },
              { text: this.$t('billing.zoom_ai_companion.new_plan_card.features_item10') },
            ],
          },
          { type: 'divide' },
          {
            category: this.$t('billing.zone.meetings'),
            categoryIconName: 'meetings',
            features: [
              { text: this.$t('billing.zone.per_meeting_minutes') },
              { text: this.$t('billing.zone.per_meeting_participants') },
            ],
          },
        ],
      };
    },
    plans() {
      return [this.aicPlan, this.proPlan];
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        const entryPoint = this.getItmMediumType();
        val &&
          this.$tracking.pricingInteractionWithOptions(
            'aic companion basic',
            `${this.trackingPlanName} - select a plan modal - impression`,
            undefined,
            { entryPoint },
          );
      },
    },
  },
  beforeCreate() {
    this.loadingId = null;
  },
  methods: {
    getItmMediumType() {
      const params = new URLSearchParams(window.location.search);
      const medium = params.get('itm_medium');
      switch (medium) {
        case 'HD':
          return 'home destination';
        case 'email':
          return 'email';
        case 'client':
          return 'client';
        case 'web':
          return 'web';
        default:
          return '';
      }
    },
    handleClose() {
      this.$tracking.pricingInteractionWithOptions(
        'aic companion basic',
        `${this.trackingPlanName} - select a plan modal - close`,
        undefined,
      );
      this.visible = false;
      this.$emit('close');
    },
    closeDialog() {
      this.visible = false;
    },
    goToAIC() {
      this.loadingId = PLAN_ID_MAP.AIC;
      const search = new URLSearchParams(window.location.search);
      const itmParams = new URLSearchParams();
      const needList = ['source'];
      search.forEach((value, key) => {
        if (key.startsWith('itm_') || needList.includes(key)) {
          itmParams.set(key, value);
        }
      });
      if (this.$context?.is?.modal) {
        for (const key of MODAL_QUERY_PARAMS) {
          const value = search.get(key);
          if (value && !itmParams.has(key)) {
            itmParams.set(key, value);
          }
        }
        if (!itmParams.has(URL_PAGE_TYPE_KEY)) {
          itmParams.set(URL_PAGE_TYPE_KEY, PageTypes.modal);
        }
      }
      requestAnimationFrame?.(() => {
        navigateCurrentWindowTo(`${AIC_UNIFIED_LINK}&${itmParams.toString()}`, { replace: false })
      });
    },
    handleBuy(planId) {
      const selectedPlan = this.plans.find((plan) => plan.id === planId);
      if (selectedPlan?.trackingName) {
        this.$tracking.pricingInteractionWithOptions(
          'aic companion basic',
          `select a plan modal - ${selectedPlan.trackingName} - buy now`,
        );
      }
      switch (planId) {
        case PLAN_ID_MAP.PRO:
          this.closeDialog();
          break;
        case PLAN_ID_MAP.AIC:
          this.goToAIC();
          break;
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/modal.scss';
@import '@/styles/variables.scss';

.opc-aic-pro-union-dialog {
  .zbo-dialog__header {
    .zbo-dialog__title {
      font-size: 20px;
      font-weight: 590;
      line-height: 24px;
      letter-spacing: -0.45px;
    }
  }
  .zbo-dialog__close.zm-icon-close {
    font-size: 14px;
  }
  .zbo-dialog {
    border-radius: 16px;
  }
  .zbo-dialog__body {
    padding-top: 24px;
  }
  .dialog-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    justify-content: center;
    gap: 0;
  }
}
</style>
